// JSON-LD buildery – iba pravdivé údaje dostupné na webe.
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, programAreas } from "./config";
import { candidate, social } from "../mock";

const PERSON_ID = `${SITE_URL}/#osoba`;
const WEBSITE_ID = `${SITE_URL}/#web`;

// Oficiálne profily (len čisté, overiteľné URL).
const sameAs = social
  .map((s) => s.href)
  .filter((h) => h && h.startsWith("http") && !h.includes("google.com/goto"));

export const personNode = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Ján Kuráň",
  url: `${SITE_URL}/o-mne`,
  image: DEFAULT_OG_IMAGE,
  jobTitle: "Kandidát na primátora mesta Ružomberok",
  description:
    "Ján Kuráň je kandidát na primátora mesta Ružomberok v komunálnych voľbách 2026. Volebné číslo 3.",
  homeLocation: {
    "@type": "Place",
    name: "Ružomberok, Slovensko",
  },
  ...(sameAs.length ? { sameAs } : {}),
};

export const websiteNode = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: SITE_NAME,
  inLanguage: "sk",
  about: { "@id": PERSON_ID },
};

export const buildWebPage = ({ path, title, description }) => ({
  "@type": "WebPage",
  "@id": `${SITE_URL}${path === "/" ? "/" : path}#webpage`,
  url: `${SITE_URL}${path === "/" ? "" : path}`,
  name: title,
  description,
  inLanguage: "sk",
  isPartOf: { "@id": WEBSITE_ID },
  about: { "@id": PERSON_ID },
});

export const buildBreadcrumb = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path === "/" ? "" : it.path}`,
  })),
});

// Homepage graf: WebSite + Person + WebPage.
export const homeGraph = (page) => ({
  "@context": "https://schema.org",
  "@graph": [websiteNode, personNode, buildWebPage(page)],
});

// ProfilePage pre stránku O mne.
export const profileGraph = (page) => ({
  "@context": "https://schema.org",
  "@graph": [
    websiteNode,
    personNode,
    {
      "@type": "ProfilePage",
      "@id": `${SITE_URL}/o-mne#profilepage`,
      url: `${SITE_URL}/o-mne`,
      name: page.title,
      description: page.description,
      inLanguage: "sk",
      isPartOf: { "@id": WEBSITE_ID },
      mainEntity: { "@id": PERSON_ID },
    },
    buildBreadcrumb([
      { name: "Domov", path: "/" },
      { name: "O mne", path: "/o-mne" },
    ]),
  ],
});

// Zoznam programových oblastí ako ItemList.
export const programItemList = () => ({
  "@type": "ItemList",
  name: "Volebný program Jána Kuráňa – oblasti",
  itemListElement: programAreas.map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: a.title,
    url: a.url,
  })),
});

export const programGraph = (page) => ({
  "@context": "https://schema.org",
  "@graph": [
    websiteNode,
    personNode,
    { ...buildWebPage(page), mainEntity: programItemList() },
    buildBreadcrumb([
      { name: "Domov", path: "/" },
      { name: "Program", path: "/program" },
    ]),
  ],
});

// Article pre detail programovej oblasti.
export const areaGraph = (area, page) => ({
  "@context": "https://schema.org",
  "@graph": [
    websiteNode,
    personNode,
    {
      "@type": "Article",
      "@id": `${area.url}#article`,
      headline: `${area.title} – volebný program Jána Kuráňa pre Ružomberok`,
      description: page.description,
      inLanguage: "sk",
      author: { "@id": PERSON_ID },
      about: { "@id": PERSON_ID },
      isPartOf: { "@id": WEBSITE_ID },
      mainEntityOfPage: area.url,
    },
    buildBreadcrumb([
      { name: "Domov", path: "/" },
      { name: "Program", path: "/program" },
      { name: area.title, path: `/program/${area.slug}` },
    ]),
  ],
});

// FAQ – iba fakty z webu a programu.
export const faqGraph = (faqItems) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Zoznam aktualít (/aktuality).
export const newsListGraph = (page, items) => ({
  "@context": "https://schema.org",
  "@graph": [
    websiteNode,
    personNode,
    {
      ...buildWebPage(page),
      mainEntity: {
        "@type": "ItemList",
        itemListElement: items.map((n, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: n.title,
          url: `${SITE_URL}/aktuality/${n.slug}`,
        })),
      },
    },
    buildBreadcrumb([
      { name: "Domov", path: "/" },
      { name: "Aktuality", path: "/aktuality" },
    ]),
  ],
});

// Detail aktuality (NewsArticle).
export const newsArticleGraph = (post) => ({
  "@context": "https://schema.org",
  "@graph": [
    websiteNode,
    personNode,
    {
      "@type": "NewsArticle",
      "@id": `${SITE_URL}/aktuality/${post.slug}#article`,
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      inLanguage: "sk",
      author: { "@id": PERSON_ID },
      publisher: { "@id": WEBSITE_ID },
      mainEntityOfPage: `${SITE_URL}/aktuality/${post.slug}`,
    },
    buildBreadcrumb([
      { name: "Domov", path: "/" },
      { name: "Aktuality", path: "/aktuality" },
      { name: post.title, path: `/aktuality/${post.slug}` },
    ]),
  ],
});
